/**
 * The Settings tab's working copy: what the brand has typed but not saved yet, and how it turns
 * into the one request the Save button sends. Pure, so the tab only holds state and wires events.
 */

import type { SalesAgentSettings, UpdateSalesAgentSettingsRequestDto } from './types';

/** One conversation starter field. */
export interface StarterDraft {
	/** Local identity for React keys, so removing a field never hands its DOM node to the next. Never sent. */
	id: string;
	text: string;
}

export interface SettingsDraft {
	performanceEnabled: boolean;
	disclaimerEnabled: boolean;
	disclaimerText: string;
	subtitle: string;
	starters: StarterDraft[];
}

/**
 * Every part this tab edits, cleaned: what a save sends, and what two states are compared by.
 * The discount limits have their own tab and save, so they are not part of it.
 */
export type SettingsValues = Required<
	Pick<UpdateSalesAgentSettingsRequestDto, 'performanceEnabled' | 'disclaimer' | 'subtitle' | 'starterQuestions'>
>;

let starterSequence = 0;

export const toStarterDraft = (text: string): StarterDraft => {
	starterSequence += 1;
	return { id: `starter-question-${starterSequence}`, text };
};

/** The fields for a saved list: always at least one, empty, to type into. */
export const toStarterDrafts = (questions: string[]): StarterDraft[] =>
	(questions.length ? questions : ['']).map(toStarterDraft);

/** Trimmed, blanks and repeats dropped: what is saved. */
export const cleanStarters = (starters: StarterDraft[]): string[] => [
	...new Set(starters.map((starter) => starter.text.trim()).filter(Boolean))
];

export const toSettingsDraft = (settings: SalesAgentSettings): SettingsDraft => ({
	performanceEnabled: settings.performanceEnabled ?? true,
	disclaimerEnabled: settings.disclaimer.enabled,
	disclaimerText: settings.disclaimer.text,
	subtitle: settings.subtitle,
	starters: toStarterDrafts(settings.starterQuestions)
});

export const draftValues = (draft: SettingsDraft): SettingsValues => ({
	performanceEnabled: draft.performanceEnabled,
	disclaimer: { enabled: draft.disclaimerEnabled, text: draft.disclaimerText.trim() },
	subtitle: draft.subtitle.trim(),
	starterQuestions: cleanStarters(draft.starters)
});

export const savedValues = (settings: SalesAgentSettings): SettingsValues => ({
	performanceEnabled: settings.performanceEnabled ?? true,
	disclaimer: { enabled: settings.disclaimer.enabled, text: settings.disclaimer.text.trim() },
	subtitle: settings.subtitle.trim(),
	starterQuestions: settings.starterQuestions
});

const sameList = (a: string[], b: string[]): boolean => a.length === b.length && a.every((item, i) => item === b[i]);

export const sameValues = (a: SettingsValues, b: SettingsValues): boolean =>
	a.performanceEnabled === b.performanceEnabled &&
	a.disclaimer.enabled === b.disclaimer.enabled &&
	a.disclaimer.text === b.disclaimer.text &&
	a.subtitle === b.subtitle &&
	sameList(a.starterQuestions, b.starterQuestions);
